import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Header } from './components/Header';
import { BottomNav } from './components/BottomNav';
import { OfflineBanner } from './components/OfflineBanner';
import './styles/global.css';

// Low-bandwidth route code splitting
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const LibraryPage = lazy(() => import('./pages/LibraryPage').then((m) => ({ default: m.LibraryPage })));
const CourseDetailPage = lazy(() => import('./pages/CourseDetailPage').then((m) => ({ default: m.CourseDetailPage })));
const LessonPage = lazy(() => import('./pages/LessonPage').then((m) => ({ default: m.LessonPage })));
const MyLearningPage = lazy(() => import('./pages/MyLearningPage').then((m) => ({ default: m.MyLearningPage })));
const ProgressPage = lazy(() => import('./pages/ProgressPage').then((m) => ({ default: m.ProgressPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const AuthSsoCallbackPage = lazy(() =>
  import('./pages/AuthSsoCallbackPage').then((m) => ({ default: m.AuthSsoCallbackPage }))
);
const AuthoringDashboardPage = lazy(() =>
  import('./pages/AuthoringDashboardPage').then((m) => ({ default: m.AuthoringDashboardPage }))
);
const AuthoringCourseEditorPage = lazy(() =>
  import('./pages/AuthoringCourseEditorPage').then((m) => ({ default: m.AuthoringCourseEditorPage }))
);

const RouteLoading: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', padding: 'var(--space-4) 0' }}>
    <div className="card skeleton" style={{ height: '60px' }} />
    <div className="card skeleton" style={{ height: '240px' }} />
  </div>
);

const NotFoundPage: React.FC = () => (
  <div className="card" style={{ textAlign: 'center', padding: 'var(--space-10) var(--space-4)' }}>
    <div style={{ fontSize: '3rem', marginBottom: 'var(--space-2)' }}>🧭</div>
    <h2>Page Not Found</h2>
    <p style={{ color: 'var(--text-secondary)', margin: 'var(--space-2) 0 var(--space-4) 0' }}>
      The page you were looking for doesn't exist or has moved.
    </p>
    <Link to="/" className="btn-primary">
      Return Home
    </Link>
  </div>
);

export const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app-layout">
          <OfflineBanner />
          <Header />
          <main className="main-content">
            <Suspense fallback={<RouteLoading />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/courses/:courseId" element={<CourseDetailPage />} />
                <Route path="/courses/:courseId/lessons/:lessonId" element={<LessonPage />} />
                <Route path="/my-learning" element={<MyLearningPage />} />
                <Route path="/progress" element={<ProgressPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/sso" element={<AuthSsoCallbackPage />} />
                <Route path="/authoring" element={<AuthoringDashboardPage />} />
                <Route path="/authoring/courses/:courseId" element={<AuthoringCourseEditorPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </main>
          <BottomNav />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
